/**
 * InviteUserModal Component
 * Professional modal for inviting users to collaborate on documents
 * Features email/mobile input, role selection, expiration settings, and validation
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { 
  XMarkIcon, 
  UserPlusIcon, 
  EnvelopeIcon, 
  DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { CollaborationUser } from '../../edgedocs/types/Collaboration.types';
import { UserInvitation } from '../stores/useDocumentCollaborationStore';

export interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (invitation: UserInvitation) => Promise<void>;
  documentId: string;
  documentTitle?: string;
  currentUser?: CollaborationUser;
  existingCollaborators?: CollaborationUser[];
  pendingInvitations?: Array<{ email: string; status: string; role: string; }>;
}

interface FormErrors {
  email?: string;
  mobile?: string;
  role?: string;
  general?: string;
}

// Role descriptions for UI
const ROLE_DESCRIPTIONS = {
  editor: {
    title: 'Editor',
    description: 'Can edit document, add comments, and track changes',
    icon: '✏️',
    permissions: ['Edit document', 'Add comments', 'Track changes', 'View all content'],
  },
  reviewer: {
    title: 'Reviewer',
    description: 'Can add comments and view changes (read-only editing)',
    icon: '👀',
    permissions: ['Add comments', 'View changes', 'View all content'],
  },
  viewer: {
    title: 'Viewer',
    description: 'Read-only access to document',
    icon: '📖',
    permissions: ['View document', 'Download (if allowed)'],
  },
};

// Expiration options
const EXPIRATION_OPTIONS = [
  { value: 24, label: '24 hours' },
  { value: 72, label: '3 days' },
  { value: 168, label: '7 days (recommended)' },
  { value: 336, label: '2 weeks' },
  { value: 720, label: '30 days' },
];

export const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  onInvite,
  documentId,
  documentTitle,
  currentUser,
  existingCollaborators = [],
  pendingInvitations = [],
}) => {
  const [formData, setFormData] = useState<UserInvitation>({
    email: '',
    mobile: '',
    role: 'viewer',
    message: '',
    expiresIn: 168, // 7 days default
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setFormData({
        email: '',
        mobile: '',
        role: 'viewer',
        message: '',
        expiresIn: 168,
      });
      setErrors({});
      setIsSubmitting(false);
      setShowAdvanced(false);
    }
  }, [isOpen]);

  // Validation functions
  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) {
      return 'Email address is required';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }

    // Check if user is already a collaborator
    const isExistingCollaborator = existingCollaborators.some(
      collaborator => collaborator.email.toLowerCase() === email.toLowerCase()
    );
    if (isExistingCollaborator) {
      return 'This user is already a collaborator on this document';
    }

    // Check if there's already a pending invitation
    const hasPendingInvitation = pendingInvitations.some(
      invitation => invitation.email.toLowerCase() === email.toLowerCase() && 
                   invitation.status === 'pending'
    );
    if (hasPendingInvitation) {
      return 'This user already has a pending invitation. Please wait for them to accept or cancel the existing invitation.';
    }

    // Check if trying to invite self
    if (currentUser && currentUser.email.toLowerCase() === email.toLowerCase()) {
      return 'You cannot invite yourself';
    }

    return undefined;
  };

  const validateMobile = (mobile: string): string | undefined => {
    if (!mobile.trim()) return undefined; // Mobile is optional
    
    // Basic phone number validation (allows various formats)
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    const cleanPhone = mobile.replace(/[\s\-\(\)\.]/g, '');
    
    if (!phoneRegex.test(cleanPhone)) {
      return 'Please enter a valid phone number';
    }

    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate email
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    // Validate mobile (optional)
    const mobileError = validateMobile(formData.mobile || '');
    if (mobileError) newErrors.mobile = mobileError;

    // Validate role
    if (!formData.role) {
      newErrors.role = 'Please select a role';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    console.log('🚀 [InviteUserModal] Form submitted with data:', formData);
    
    const isValid = validateForm();
    console.log('✅ [InviteUserModal] Form validation result:', isValid);
    
    if (!isValid) {
      console.log('❌ [InviteUserModal] Form validation failed, not calling onInvite');
      return;
    }

    console.log('📧 [InviteUserModal] Calling onInvite callback...');
    setIsSubmitting(true);
    setErrors({});

    try {
      await onInvite(formData);
      console.log('✅ [InviteUserModal] onInvite completed successfully');
      // Success - modal will be closed by parent component
    } catch (error) {
      console.error('❌ [InviteUserModal] onInvite failed:', error);
      setErrors({
        general: error instanceof Error ? error.message : 'Failed to send invitation. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field: keyof UserInvitation, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear errors for the field being edited
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity lex-overlay-enter"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative transform rounded-lg bg-white dark:bg-gray-800 px-6 py-6 text-left shadow-xl transition-all w-full max-w-lg my-8 z-[60] lex-modal-enter">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="bg-lex-accent-soft p-2 rounded-lg">
                <UserPlusIcon className="w-6 h-6 text-lex-accent" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Invite Collaborator
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {documentTitle ? `Add someone to "${documentTitle}"` : 'Add someone to this document'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* General Error */}
          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
                <span className="text-sm text-red-700 dark:text-red-300">{errors.general}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email Address *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`block w-full pl-10 pr-3 py-2 border rounded-md focus:ring-lex-accent focus:border-lex-accent ${
                    errors.email 
                      ? 'border-red-300 dark:border-red-600' 
                      : 'border-gray-300 dark:border-gray-600'
                  } dark:bg-gray-700 dark:text-white`}
                  placeholder="colleague@lawfirm.com"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
              )}
            </div>

            {/* Mobile Input (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Mobile Number (Optional)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DevicePhoneMobileIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  value={formData.mobile || ''}
                  onChange={(e) => handleInputChange('mobile', e.target.value)}
                  className={`block w-full pl-10 pr-3 py-2 border rounded-md focus:ring-lex-accent focus:border-lex-accent ${
                    errors.mobile 
                      ? 'border-red-300 dark:border-red-600' 
                      : 'border-gray-300 dark:border-gray-600'
                  } dark:bg-gray-700 dark:text-white`}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              {errors.mobile && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.mobile}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                SMS notifications for urgent updates and reminders
              </p>
            </div>

            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Permission Level *
              </label>
              <div className="space-y-2">
                {Object.entries(ROLE_DESCRIPTIONS).map(([roleKey, roleInfo]) => (
                  <label
                    key={roleKey}
                    className={`block p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                      formData.role === roleKey
                        ? 'border-lex-accent bg-lex-accent-soft'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="role"
                        value={roleKey}
                        checked={formData.role === roleKey}
                        onChange={(e) => handleInputChange('role', e.target.value)}
                        className="h-4 w-4 text-lex-accent focus:ring-lex-accent border-gray-300"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{roleInfo.icon}</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {roleInfo.title}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {roleInfo.description}
                        </p>
                        
                        {/* Show permissions for selected role */}
                        {formData.role === roleKey && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Permissions included:
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {roleInfo.permissions.map((permission, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                                >
                                  <CheckCircleIcon className="w-3 h-3 mr-1" />
                                  {permission}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.role}</p>
              )}
            </div>

            {/* Advanced Settings Toggle */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center space-x-2 text-sm text-lex-accent hover:text-lex-accent-hover"
              >
                <InformationCircleIcon className="w-4 h-4" />
                <span>{showAdvanced ? 'Hide' : 'Show'} advanced options</span>
              </button>
            </div>

            {/* Advanced Settings */}
            {showAdvanced && (
              <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                {/* Personal Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Personal Message (Optional)
                  </label>
                  <textarea
                    value={formData.message || ''}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                    placeholder="Hi, I'd like you to review this document and provide your feedback..."
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    This message will be included in the invitation email
                  </p>
                </div>

                {/* Expiration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Invitation Expires
                  </label>
                  <select
                    value={formData.expiresIn}
                    onChange={(e) => handleInputChange('expiresIn', Number(e.target.value))}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                  >
                    {EXPIRATION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    The invitation link will expire after this time period
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!formData.email || isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-lex-accent rounded-md hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <UserPlusIcon className="w-4 h-4" />
                    <span>Send Invitation</span>
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Info Footer */}
          <div className="mt-4 p-3 bg-lex-accent-soft rounded-lg">
            <div className="flex items-start space-x-2">
              <InformationCircleIcon className="w-5 h-5 text-lex-accent mt-0.5 flex-shrink-0" />
              <div className="text-sm text-lex-accent">
                <p className="font-medium mb-1">What happens next?</p>
                <ul className="text-xs space-y-1">
                  <li>• The invitee will receive an email with a secure invitation link</li>
                  <li>• They can join the document collaboration by clicking the link</li>
                  <li>• You'll be notified when they accept the invitation</li>
                  {formData.mobile && <li>• SMS reminders will be sent for urgent updates</li>}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default InviteUserModal; 